// Where the app moves on its own: DOWN when there is no choice to make, and UP
// when the user asks to go back.
//
// ⚠️ BOTH DIRECTIONS LIVE HERE BECAUSE THEY ARE ONE MECHANISM. Skipping a level
// on the way in and returning to it on the way out are the same fact seen
// twice, and writing them apart produces a loop rather than a bug you can see:
// back lands on a rooms screen holding a single room, that screen drills
// forward again because it always does, and the user can never leave the
// device. `viaBack` is the whole answer — a level entered by going back does
// not drill.
//
// ── The structure ──────────────────────────────────────────────────────────
//
//   /home  →  /rooms/:locationId  →  /room/:roomId/devices  →  /device/:uno
//
// ⚠️ BACK IS COMPUTED, NOT POPPED. `navigate(-1)` is browser history, which
// agrees with the structure only when the user walked in the long way — and
// after a drill they never have, because a drill replaces rather than pushes.
// It also disagrees after a deep link, a reload, or an open from Favourites.
// The full account of why this was moved off history is in deviceNav.js.
//
// ── The drill rule ─────────────────────────────────────────────────────────
//
// "No choice" means ONE THING TO TAP, counted across every list on the screen
// at once. A location holding one room AND one common device is a choice — two
// different kinds of thing, both tappable — so it stops. Counting only the
// rooms there would walk the user past a device they can see on the screen.

import { backTargetFor } from '../devices/deviceNav';

/** Did the user arrive here by going back? Then do not drill forward again. */
export const arrivedViaBack = (state) => !!(state && state.viaBack);

// A device's nav state. `from` is what lets it come back correctly: the row it
// is handed carries RoomID but no LocationID, and the room list needs the
// location or it silently drops whole device types (see deviceNav.js).
const deviceTarget = (device, from) => ({
  path: `/device/${device.UniqueNumber}`,
  state: { device, from },
});

/**
 * The user's locations → the one to open, or null to let them choose.
 */
export function drillFromLocations(locations) {
  const list = locations || [];
  if (list.length !== 1) return null;
  const l = list[0];
  if (l.LocationID == null) return null;
  return {
    path: `/rooms/${l.LocationID}`,
    state: { locationName: l.LocationName || '' },
  };
}

/**
 * A location's rooms AND its common devices → the one thing to open.
 *
 * Both lists count. A common device belongs to the location rather than to any
 * room, so when it is the only thing here the drill goes straight to it and
 * skips the device list entirely — there is no room to list it under. That is
 * the "single location, single common device" case.
 */
export function drillFromRooms({
  rooms, common, locationId, locationName,
}) {
  const rs = rooms || [];
  const cs = common || [];
  if (rs.length + cs.length !== 1) return null;

  if (rs.length === 1) {
    const r = rs[0];
    if (r.RoomID == null) return null;
    return {
      path: `/room/${r.RoomID}/devices`,
      state: { roomName: r.RoomName || '', locationName, locationId },
    };
  }
  const d = cs[0];
  if (!d || !d.UniqueNumber) return null;
  // ⚠️ `roomId: null`, NOT 0. Back from this device must land on the rooms
  // screen, which is where it was shown — a common device is in no room's
  // device list, so returning to one would return to a list it is absent from.
  return deviceTarget(d, {
    locationId, locationName, roomId: null, roomName: '',
  });
}

/**
 * A room's devices AND the location's common strip above them → the one to open.
 *
 * The strip is on this screen too, so it counts here as well; a room holding one
 * device under a location with one common device is two things to tap.
 */
export function drillFromDevices({
  devices, common, roomId, roomName, locationId, locationName,
}) {
  const ds = devices || [];
  const cs = common || [];
  if (ds.length + cs.length !== 1) return null;
  const d = ds[0] || cs[0];
  if (!d || !d.UniqueNumber) return null;
  // Back goes to THIS list either way — including for a common device, because
  // this list is where the user saw it.
  return deviceTarget(d, {
    locationId, locationName, roomId, roomName,
  });
}

/**
 * The structural parent of a route → { path, state } with `viaBack` set, or
 * null when the structure is unknown and history is the only honest answer.
 *
 * `state` is the current route's nav state; for a device it carries the `from`
 * that names the room and location it was opened from.
 */
export function parentOf(pathname, state) {
  const path = String(pathname || '');
  // ⚠️ `viaBack` IS ADDED HERE, not by the callers. It is the one flag that
  // stops the destination drilling straight back down, and a caller that forgot
  // it would produce a screen the user cannot leave.
  const up = (t) => (t ? { path: t.path, state: { ...(t.state || {}), viaBack: true } } : null);

  if (/^\/device\/[^/]+\/?$/.test(path)) {
    return up(backTargetFor((state && state.device) || null, state && state.from));
  }

  if (/^\/room\/[^/]+\/devices\/?$/.test(path)) {
    const locId = state && state.locationId;
    // Without the location the rooms screen cannot be addressed at all, and
    // guessing one would open somebody else's site.
    if (locId == null || Number(locId) <= 0) return null;
    return up({
      path: `/rooms/${locId}`,
      state: { locationName: (state && state.locationName) || '' },
    });
  }

  if (/^\/rooms\/[^/]+\/?$/.test(path)) return up({ path: '/home', state: {} });

  return null;
}

// ── The home screen's four tabs ────────────────────────────────────────────
//
// `/home` is Favourites · Your Locations · Scenes · Me, switched by tapping and
// written with `replace`, so a tab change leaves NO history entry: the hardware
// back button cannot unwind them and the rule has to be stated here instead.
//
// ⚠️ YOUR LOCATIONS IS A ROOT, AND IT WAS NOT. Every non-zero tab was sent to
// Favourites, so back on Your Locations — the top of the app in the Flutter
// original, where `mainScreen.dart` showed the exit popup — quietly slid
// sideways and never asked to exit. Reported on the APK, 2026-10-04.
//
// Scenes and Me are NOT roots: they are places you go from the top, so back
// takes one step back to Favourites rather than offering to close the app.
export const HOME_TAB_FAVOURITES = 0;
export const HOME_TAB_LOCATIONS = 1;
export const HOME_ROOT_TABS = [HOME_TAB_FAVOURITES, HOME_TAB_LOCATIONS];

/**
 * What the hardware back button does on `/home`.
 *
 * -> { exit: true }        ask before closing the app
 *    { tab: <index> }      move to that tab first
 */
export function homeBack(tab) {
  const i = Number(tab);
  const current = Number.isFinite(i) && i > 0 ? Math.min(3, Math.trunc(i)) : 0;
  if (HOME_ROOT_TABS.includes(current)) return { exit: true };
  return { tab: HOME_TAB_FAVOURITES };
}
